import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { addAssessmentSchema } from 'utils/validators/assessment';
import { roundNum, arrSum } from 'utils/numbers';
import { ulid } from 'ulid';
import ErrorFocus from 'shared/components/error-focus/ErrorFocus';

import Title from 'shared/components/title/Title';
import confirmDiscardChanges from 'shared/components/confirm-discard-changes/confirmDiscardChanges';
import PageContainer from './layout/Page';
import Section from './layout/Section';

import AssessmentHead from '../AssessmentSections/AssessmentHead';
import GenderField from './form/GenderField';
import BirthdateField from './form/BirthDateField';
import WeightField from './form/WeightField';
import HeightField, { mapping as heightMapping } from './form/HeightField';
import MeasurementField from './form/MeasurementField';
import ProgressPhotoField from './form/ProgressPhotoField';
import BodyFatPercentageField, {
  mapping as bodyFatPerMapping
} from './form/BodyFatPercentageField';
import MovementList from './form/MovementList';
import AssessmentLoader from './AssessmentLoader';
import { uploadFile, deleteFile } from '../../../../../api/helpers/fileUtils';

export default function AssessmentForm({
  client,
  prevUrl,
  prevTitle,
  formInitVals,
  submitAssessmentForm,
  history
}) {
  const [progress, setProgress] = useState(0);
  const [toBeDeletedFiles, setToBeDeletedFiles] = useState([]);

  const addToFilesToBeDeleted = file => {
    setToBeDeletedFiles([...toBeDeletedFiles, file]);
  };

  const setAssessment = values => {
    const bodyFatPercentageIsText =
      values.body_fat_percentage.method === bodyFatPerMapping.textMethod.value;

    var assessment = {
      ...(values.id && { id: values.id }),
      ...(values.trainer_id && { trainer_id: values.trainer_id }),
      ...(values.create_at && { create_at: values.create_at }),
      client_id: client.id,
      assessment_type: values.assessment_type,
      birthday: new Date(
        `${values.birthdate.month}-${values.birthdate.day}-${values.birthdate.year}`
      ),
      gender: values.gender,
      weight: values.weight,
      height: {
        measurement_unit: values.height.unit,
        value:
          values.height.unit === heightMapping.metricUnit.value
            ? Number(values.height.cm)
            : roundNum(
                Number(values.height.feet) + Number(values.height.inch) / 12
              )
      },
      measurements: values.measurements,
      progress_photos: values.progress_photos
        ? {
            front: values.progress_photos.front.url,
            side: values.progress_photos.side.url,
            back: values.progress_photos.back.url
          }
        : null,
      body_fat_percentage_is_text: bodyFatPercentageIsText,
      body_fat_percentage_is_caliper: !bodyFatPercentageIsText,
      body_fat_percentage: bodyFatPercentageIsText
        ? Number(values.body_fat_percentage.textFatPercentage)
        : Number(values.body_fat_percentage.calculatedFatPercentage),

      // body fat percentage caliper keys
      ...(!bodyFatPercentageIsText && bfpKeys(values)),

      // movement keys
      ...(values.assessment_type === 'First Session' && movementKeys(values))
    };

    let { front, side, back } = assessment.progress_photos;
    if (!front && !side && !back) {
      assessment.progress_photos = null;
    }
    return assessment;
  };

  const bfpKeys = values => {
    const caliparMethod = values.body_fat_percentage.caliperMethod;
    const measurementsKey = `${caliparMethod}_measurements`;

    return {
      [caliparMethod]: true,
      [measurementsKey]: {
        chest: Number(values.body_fat_percentage.chest),
        abdominal: Number(values.body_fat_percentage.abdominal),
        thigh: Number(values.body_fat_percentage.thigh),
        ...(caliparMethod !== bodyFatPerMapping.caliperMethod.jp3.value && {
          subscapular: Number(values.body_fat_percentage.subscapular),
          tricep: Number(values.body_fat_percentage.tricep),
          suprailiac: Number(values.body_fat_percentage.suprailiac)
        }),
        ...(caliparMethod === bodyFatPerMapping.caliperMethod.jp7.value && {
          midaxillary: Number(values.body_fat_percentage.midaxillary)
        }),
        ...(caliparMethod ===
          bodyFatPerMapping.caliperMethod.parillo9.value && {
          lower_back: Number(values.body_fat_percentage.lower_back),
          calf: Number(values.body_fat_percentage.calf),
          bicep: Number(values.body_fat_percentage.bicep)
        })
      }
    };
  };

  const movementKeys = values => {
    return {
      dowel_bodyweight_squat: movementObj(values.dowel_bodyweight_squat),
      dowel_standing_shoulder_press: movementObj(
        values.dowel_standing_shoulder_press
      ),
      dowel_standing_hip_hinge: movementObj(values.dowel_standing_hip_hinge),
      db_front_foot_elevated_split_squat: movementObj(
        values.db_front_foot_elevated_split_squat
      ),
      ng_shoulder_width_lat_pull_down: movementObj(
        values.ng_shoulder_width_lat_pull_down
      )
    };
  };

  const movementObj = obj => {
    return {
      rating_measurement_unit: Number(obj.rating_measurement_unit),
      video_url_value: obj.video.url,
      dysfunction: dysfunctionValue(obj)
    };
  };

  const dysfunctionValue = movement => {
    return Number(movement.rating_measurement_unit) === 5
      ? ''
      : Object.values(movement.dysfunction)
          .map(v => (v ? 1 : 0))
          .toString();
  };

  const uploadFiles = async values => {
    const files = [
      ['progress_photos', 'front', 'photo'],
      ['progress_photos', 'side', 'photo'],
      ['progress_photos', 'back', 'photo'],
      ['dowel_bodyweight_squat', 'video', 'video'],
      ['dowel_standing_shoulder_press', 'video', 'video'],
      ['dowel_standing_hip_hinge', 'video', 'video'],
      ['db_front_foot_elevated_split_squat', 'video', 'video'],
      ['ng_shoulder_width_lat_pull_down', 'video', 'video']
    ];

    const files_names = [
      'front',
      'side',
      'back',
      'DowelBodyweightSquat',
      'DowelStandingShoulderPress',
      'DowelStandingHipHinge',
      'DBFrontFootElevatedSplitSquat',
      'NGShoulderWidthLatPullDown',
    ]

    var uploadedSize = 0;
    const totalSize = arrSum(
      files.map(f => (values[f[0]] ? values[f[0]][f[1]].size : 0))
    );

    const uploadOneFile = async (file, index) => {
      var field = values[file[0]][file[1]];
      const onSucc = url => {
        field.url = url;
        uploadedSize += field.size;
        console.log('file uploaded successfully');
      };

      const onChange = progressVal => {
        setProgress(
          Math.ceil((100 * (uploadedSize + progressVal)) / totalSize)
        );
      };

      const onErr = err => {
        throw new Error(err);
      };

      await uploadFile(
        `clients/assessment/${file[2]}/${client.id}/${files_names[index]}`,
        field.url,
        'data_url',
        onChange,
        onErr,
        onSucc
      );
    };

    for (var i = 0; i < files.length; i++) {
      if (values[files[i][0]] && values[files[i][0]][files[i][1]].size)
        await uploadOneFile(files[i], i);
    }
  };

  const deleteOldFiles = () => {
    toBeDeletedFiles.forEach(file => {
      if (file.startsWith('https')) deleteFile(file);
    });
  };

  return (
    <PageContainer
      title={formInitVals.id ? 'Edit' : `${formInitVals.assessment_type}`}
      crumbs={[
        { url: '/', title: 'Home' },
        {
          url: `/clients/${client.id}`,
          title: `${client.first_name} ${client.last_name}`
        },
        { url: prevUrl, title: prevTitle }
      ]}
    >
      <Formik
        enableReinitialize={true}
        initialValues={formInitVals}
        onSubmit={async (values, actions) => {
          try {
            await uploadFiles(values);
            deleteOldFiles();
            submitAssessmentForm(setAssessment(values), history);
            setProgress(100);
          } catch (err) {
            console.error(err);
            actions.setSubmitting(false);
          }
        }}
        validationSchema={addAssessmentSchema}
      >
        {props => (
          <Form>
            {props.isSubmitting ? (
              <AssessmentLoader progress={progress} />
            ) : (
              <div>
                <Title
                  pageTitle={`Assessment: ${formInitVals.assessment_type}`}
                  buttonText="Save"
                  isSubmitting={props.isSubmitting}
                  linkText="Cancel"
                  cancelLink={prevUrl}
                  cancelBtn={
                    props.dirty &&
                    (() => confirmDiscardChanges(prevUrl, history))
                  }
                />

                <div className="assessment-template">
                  <AssessmentHead client={client} />

                  <Section title="General Info">
                    <div className="general-info-list">
                      <div className="general-info-row">
                        {formInitVals.assessment_type === 'First Session' && (
                          <GenderField label="Gender" fieldName="gender" />
                        )}
                        {formInitVals.assessment_type === 'First Session' && (
                          <BirthdateField
                            label="Birthdate"
                            monthFieldName="birthdate.month"
                            dayFieldName="birthdate.day"
                            yearFieldName="birthdate.year"
                          />
                        )}
                        <WeightField
                          label="Weight"
                          valueFieldName="weight.value"
                          unitFieldName="weight.measurement_unit"
                        />
                      </div>
                    </div>
                    <div className="general-info-row">
                      <div className="general-info-left">
                        <HeightField
                          cmFieldName="height.cm"
                          feetFieldName="height.feet"
                          inchFieldName="height.inch"
                          unitFieldName="height.unit"
                        />
                      </div>
                      <BodyFatPercentageField
                        bodyFatPercentageFieldName="body_fat_percentage"
                        genderFieldName="gender"
                        birthdateYearFieldName="birthdate.year"
                        weightValueFieldName="weight.value"
                        weightUnitFieldName="weight.measurement_unit"
                      />
                    </div>
                  </Section>

                  <Section title="Circumference Measurements">
                    <div className="measurement-list">
                      <MeasurementField
                        label="Chest"
                        valueFieldName="measurements.chest"
                      />
                      <MeasurementField
                        label="Waist"
                        valueFieldName="measurements.waist"
                      />
                      <MeasurementField
                        label="Hips"
                        valueFieldName="measurements.hips"
                      />
                    </div>
                    <div className="measurement-list">
                      <MeasurementField
                        label="Upper Leg"
                        valueFieldName="measurements.upper_leg"
                      />
                      <MeasurementField
                        label="Calf"
                        valueFieldName="measurements.calf"
                      />
                      <MeasurementField
                        label="Upper Arm"
                        valueFieldName="measurements.upper_arm"
                      />
                    </div>
                  </Section>

                  {formInitVals.assessment_type === 'First Session' && (
                    <Section title="Functional Movement Screening">
                      <MovementList
                        dowelBodyweightSquat="dowel_bodyweight_squat"
                        dowelStandingShoulderPress="dowel_standing_shoulder_press"
                        dowelStandingHipHinge="dowel_standing_hip_hinge"
                        dbFrontFootElevatedSplitSquat="db_front_foot_elevated_split_squat"
                        ngShoulderWidthLatPullDown="ng_shoulder_width_lat_pull_down"
                        addToFilesToBeDeleted={addToFilesToBeDeleted}
                      />
                    </Section>
                  )}

                  <Section title="Progress Photos">
                    <div className="photos-list">
                      <ProgressPhotoField
                        label="Front"
                        urlFieldName="progress_photos.front.url"
                        sizeFieldName="progress_photos.front.size"
                        addToFilesToBeDeleted={addToFilesToBeDeleted}
                      />
                      <ProgressPhotoField
                        label="Side"
                        urlFieldName="progress_photos.side.url"
                        sizeFieldName="progress_photos.side.size"
                        addToFilesToBeDeleted={addToFilesToBeDeleted}
                      />
                      <ProgressPhotoField
                        label="Back"
                        urlFieldName="progress_photos.back.url"
                        sizeFieldName="progress_photos.back.size"
                        addToFilesToBeDeleted={addToFilesToBeDeleted}
                      />
                    </div>
                  </Section>
                </div>
              </div>
            )}
            <ErrorFocus />
          </Form>
        )}
      </Formik>
    </PageContainer>
  );
}
